import { useEffect, useState, type FormEvent } from "react";
import { ChevronDown, KeyRound, Menu, Save, ShieldCheck } from "lucide-react";

import { api } from "../api";
import type { AppSettings } from "../types";

type Props = {
  onOpenSidebar: () => void;
  onToast: (message: string, kind?: "success" | "error") => void;
};

const levels = [
  ["level_1_fast", "Быстро"],
  ["level_2_balanced", "Сбалансированно"],
  ["level_3_deep", "Глубоко"],
  ["level_4_max", "Максимально"],
];

const styles = [
  ["calm_partner", "Спокойный партнёр"],
  ["concise_helper", "Краткий помощник"],
  ["thoughtful_strategist", "Вдумчивый стратег"],
  ["technical_expert", "Технический эксперт"],
  ["creative_companion", "Творческий собеседник"],
  ["gentle_coach", "Мягкий наставник"],
];

export function SettingsPage({ onOpenSidebar, onToast }: Props) {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  useEffect(() => {
    api.settings().then(setSettings).catch((error) => onToast(error.message, "error"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!settings) return;
    setBusy(true);
    try {
      const saved = await api.saveSettings({ ...settings, api_key: apiKey.trim() || undefined });
      setSettings(saved);
      setApiKey("");
      onToast("Настройки сохранены");
    } catch (error) { onToast(error instanceof Error ? error.message : "Не удалось сохранить настройки", "error"); }
    finally { setBusy(false); }
  };

  const changePassword = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await api.changePassword(currentPassword, newPassword);
      setCurrentPassword(""); setNewPassword("");
      onToast("Пароль изменён");
    } catch (error) { onToast(error instanceof Error ? error.message : "Не удалось изменить пароль", "error"); }
  };

  if (!settings) return <main className="workspace-page"><div className="page-loader">Загрузка...</div></main>;

  const update = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => setSettings({ ...settings, [key]: value });

  return (
    <main className="workspace-page">
      <header className="workspace-topbar">
        <button className="icon-button button-ghost mobile-only" aria-label="Открыть меню" onClick={onOpenSidebar}><Menu /></button>
        <div><p className="eyebrow">SuperGPT</p><h1>Настройки</h1></div>
      </header>
      <div className="workspace-content settings-content">
        <form className="settings-form" onSubmit={save}>
          <section className="settings-section">
            <header><KeyRound /><div><h2>Модель</h2><p>{settings.model_name}</p></div></header>
            <label>
              API-ключ PolzaAI
              <input type="password" autoComplete="off" placeholder={settings.api_key_configured ? settings.api_key_masked : "Введите API-ключ"} value={apiKey} onChange={(event) => setApiKey(event.target.value)} />
            </label>
          </section>

          <section className="settings-section">
            <header><span className="settings-symbol">Aa</span><div><h2>Общение</h2></div></header>
            <fieldset>
              <legend>Уровень мышления</legend>
              <div className="segmented-control">
                {levels.map(([id, label]) => <button className={settings.thinking_level === id ? "active" : ""} type="button" key={id} onClick={() => update("thinking_level", id)}>{label}</button>)}
              </div>
            </fieldset>
            <label>
              Готовый стиль
              <select value={settings.style_preset} onChange={(event) => update("style_preset", event.target.value)}>
                {styles.map(([id, label]) => <option value={id} key={id}>{label}</option>)}
              </select>
            </label>
            <label>
              Как агент должен со мной общаться
              <textarea rows={4} maxLength={4000} value={settings.communication_prompt} onChange={(event) => update("communication_prompt", event.target.value)} />
            </label>
            <label>
              Свой стиль общения
              <textarea rows={3} maxLength={4000} value={settings.custom_style} onChange={(event) => update("custom_style", event.target.value)} />
            </label>
            <label>
              Дополнительная инструкция
              <textarea rows={3} maxLength={4000} value={settings.additional_instruction} onChange={(event) => update("additional_instruction", event.target.value)} />
            </label>
          </section>

          <details className="advanced-settings">
            <summary><span>Расширенные настройки</span><ChevronDown /></summary>
            <div className="advanced-grid">
              <label>Температура <input type="number" min="0" max="2" step="0.1" value={settings.temperature} onChange={(event) => update("temperature", Number(event.target.value))} /></label>
              <label>Максимум токенов <input type="number" min="256" max="384000" step="256" value={settings.max_completion_tokens} onChange={(event) => update("max_completion_tokens", Number(event.target.value))} /></label>
              <label>ID модели <input value={settings.model_id} readOnly /></label>
            </div>
          </details>

          <button className="button button-primary save-settings" type="submit" disabled={busy}><Save /> {busy ? "Сохраняем..." : "Сохранить"}</button>
        </form>

        <form className="settings-section password-form" onSubmit={changePassword}>
          <header><ShieldCheck /><div><h2>Пароль</h2></div></header>
          <div className="password-grid">
            <label>Текущий пароль <input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} required /></label>
            <label>Новый пароль <input type="password" minLength={10} value={newPassword} onChange={(event) => setNewPassword(event.target.value)} required /></label>
          </div>
          <button className="button button-glass" type="submit">Изменить пароль</button>
        </form>
      </div>
    </main>
  );
}

